<template>
  <div id="goods-return-note-edit" class="group-setup">
    <v-progress-linear
      indeterminate
      color="blue"
      v-if="loading"
    ></v-progress-linear>

    <v-layout row v-if="errors">
      <v-flex>
        <Alert @dismissed="onDismissed" :text="errors"></Alert>
      </v-flex>
    </v-layout>

    <v-form v-if="!loading">
      <v-row>
        <v-col cols="12" md="12" sm="12">
          <v-card class="pa-5" flat>
            <HeaderDividerBlock headerText="Edit Goods Return Note" />
            <v-row>
              <v-col cols="6" sm="6">
                <v-col class="pa-0 ma-0">
                  <p class="text-title">Purchase Order Number</p>
                </v-col>
                <v-autocomplete
                  outlined
                  dense
                  v-model="form.po_number"
                  required
                  clearable
                  placeholder="Select PO"
                  :items="purchaseOrders"
                  item-text="po_number"
                  item-value="po_number"
                >
                </v-autocomplete>
              </v-col>

              <v-col cols="6" sm="6">
                <v-col class="pa-0 ma-0">
                  <p class="text-title">Goods Return Date</p>
                </v-col>
                <DatePicker
                  v-model="form.goods_return_date"
                  placeholder="Goods Return Date"
                  :max="new Date().toISOString().substr(0, 10)"
                />
              </v-col>

              <v-col cols="6" sm="6">
                <v-col class="pa-0 ma-0">
                  <p class="text-title">Supplier</p>
                </v-col>
                <v-autocomplete
                  outlined
                  dense
                  v-model="form.supplier_id"
                  required
                  clearable
                  placeholder="Select Supplier"
                  :items="suppliers"
                  item-text="name"
                  item-value="id"
                ></v-autocomplete>
              </v-col>

              <v-col cols="12" sm="12">
                <v-col class="pa-0 ma-0">
                  <p class="text-title">Reason for Return</p>
                </v-col>
                <v-text-field
                  outlined
                  dense
                  v-model="form.reason_for_return"
                  required
                  clearable
                  placeholder="Reason for Return"
                ></v-text-field>
              </v-col>
            </v-row>

            <v-col cols="12" md="12" sm="12">
              <GoodsReturnEditItems
                :products="products"
                :batches="batches"
                :initialGriItems="products"
                :key="form.po_number"
                @total="handleTotal"
                @items="handleItems"
              ></GoodsReturnEditItems>
            </v-col>

            <v-col cols="12" md="6" sm="12">
              <v-col>
                <v-col class="ma-0 pa-0">
                  <p class="text-h5 font-weight-bold">Summary</p>
                </v-col>
                <v-col class="px-7 pa-10 total-section">
                  <v-row class="justify-space-between">
                    <p class="text-subtitle-1 font-weight-bold">Sub Total</p>
                    <p>{{ calculateSubtotal() }}</p>
                  </v-row>
                </v-col>
              </v-col>
            </v-col>

            <v-card-actions class="mt-10">
              <v-row>
                <v-col cols="12" md="10" xs="6"></v-col>
                <v-col cols="12" md="2" xs="6">
                  <v-btn
                    v-if="hasPermission('goods_return_note.edit')"
                    large
                    color="#00B3FF"
                    dark
                    depressed
                    block
                    @click="submit"
                  >
                    Update
                  </v-btn>
                </v-col>
              </v-row>
            </v-card-actions>
          </v-card>
        </v-col>
      </v-row>
    </v-form>

    <Snackbars
      :text="successMsg"
      :snackbar="snackbar"
      :color="'success'"
      @classSnackbar="closeSnackbar"
    />
  </div>
</template>

<script>
import DatePicker from "@/components/shared/DatePicker";
import Alert from "@/components/shared/Alert";
import Snackbars from "@/components/shared/Snackbars";
import HeaderDividerBlock from "@/components/shared/HeaderDividerBlock";
import { GoodsReturnService } from "@/services/goodsReturn-service.js";
import GoodsReturnEditItems from "@/components/GoodsReturn/GoodsReturnEditItems";
import { checkError } from "@/config/errors.js";
import { createNamespacedHelpers } from "vuex";
const { mapGetters } = createNamespacedHelpers("auth");

export default {
  name: "EditGoodsReturnNoteForm",
  components: {
    Alert,
    Snackbars,
    DatePicker,
    HeaderDividerBlock,
    GoodsReturnEditItems,
  },
  data: () => ({
    loading: false,
    errors: null,
    snackbar: false,
    successMsg: "",
    form: {
      po_number: "",
      goods_return_date: new Date().toISOString().substr(0, 10),
      reason_for_return: "",
      supplier_id: "",
      sub_total: 0.0,
      items: [],
    },
    invoices: [],
    products: [],
    suppliers: [],
    batches: [],
    search: "",
    id: "",
    statusOptions: ["pending", "completed", "cancelled"],
  }),
  created() {
    this.fetchData();
    this.loadGoodsReturnNote();
    this.fetchPurchaseOrders();
    this.fetchBatches();
  },

  watch: {
    "form.po_number": {
      immediate: false,
      handler(newPoNumber) {
        if (newPoNumber) {
          this.fetchProductsForPo(newPoNumber);
        } else {
          this.products = [];
        }
      },
    },
  },

  computed: {
    ...mapGetters({
      hasPermission: "hasPermission",
    }),
    dateValidation() {
      return (value) => {
        if (new Date(value) > new Date()) {
          return "Date cannot be in the future";
        }
        return true;
      };
    },
  },
  methods: {
    async fetchBatches() {
      try {
        this.loadingBatches = true;
        const batches = await GoodsReturnService.getBatchList(this.search);
        this.batches = batches.data.data;
      } catch (error) {
        console.error(error);
        let data = error.response.data;
        let errorOutput = checkError.errorFormatting(data);
        this.errors = errorOutput;
      } finally {
        this.loadingBatches = false;
      }
    },
    async fetchPurchaseOrders() {
      try {
        const purchaseOrders = await GoodsReturnService.getPurchaseOrders();
        this.purchaseOrders = purchaseOrders.data;
      } catch (error) {
        console.error(error);
        let data = error.response.data;
        let errorOutput = checkError.errorFormatting(data);
        this.errors = errorOutput;
      } finally {
        this.loading = false;
      }
    },
    async fetchData() {
      try {
        this.loading = true;
        const [suppliers] = await Promise.all([
          GoodsReturnService.getSuppliersList(),
        ]);
        this.suppliers = suppliers.data;
      } catch (error) {
        console.error(error);
        let data = error.response.data;
        let errorOutput = checkError.errorFormatting(data);
        this.errors = errorOutput;
      } finally {
        this.loading = false;
      }
    },

    async fetchProductsForPo(poNumber) {
      try {
        this.loading = true;
        const response = await GoodsReturnService.getItemsByPoNumber(poNumber);
        this.products = response.data.data;
      } catch (error) {
        console.error(error);
        let data = error.response.data;
        let errorOutput = checkError.errorFormatting(data);
        this.errors = errorOutput;
      } finally {
        this.loading = false;
      }
    },

    async loadGoodsReturnNote() {
      try {
        const goodsReturnNote = await GoodsReturnService.getGrnDetails(
          this.$route.params.uuid
        );
        const goodsReturnNoteItems = await GoodsReturnService.getItemsByGrnId(
          goodsReturnNote.id
        );

        if (goodsReturnNote) {
          const grnData = goodsReturnNote;
          (this.id = grnData.id), (this.form.po_number = grnData.po_number);
          this.form.goods_return_date = grnData.goods_return_date;
          this.form.reason_for_return = grnData.reason_for_return;
          this.form.supplier_id = grnData.supplier_id;
          this.form.sub_total = grnData.sub_total;
          this.products = goodsReturnNoteItems;
        }
      } catch (error) {
        console.error(error);
        let data = error.response.data;
        let errorOutput = checkError.errorFormatting(data);
        this.errors = errorOutput;
      } finally {
        this.loading = false;
      }
    },
    handleItems(items) {
      this.form.items = items;
    },
    handleTotal(total) {
      this.form.grand_total = total;
    },
    calculateSubtotal() {
      let subtotal = 0;
      this.form.items.forEach((item) => {
        subtotal += item.quantity_returned * item.unit_price;
      });
      this.form.sub_total = subtotal;
      return parseFloat(subtotal).toFixed(2);
    },
    async submit() {
      try {
        this.loading = true;
        this.errors = null;
        const response = await GoodsReturnService.updateGrn(this.id, this.form);
        this.successMsg = response.data.message;
        this.snackbar = true;
        this.resetForm();
        this.$router.push({ name: "GoodsReturn" });
      } catch (error) {
        console.error(error);
        let data = error.response.data;
        let errorOutput = checkError.errorFormatting(data);
        this.errors = errorOutput;
      } finally {
        this.loading = false;
      }
    },
    resetForm() {
      this.form = {
        goods_return_date: new Date().toISOString().substr(0, 10),
        supplier_id: "",
        reason_for_return: "",
        sub_total: 0.0,
        items: [],
      };
    },
    closeSnackbar() {
      this.snackbar = false;
    },
  },
};
</script>
